import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { closeSetupMonitoringForm } from "../../actions/campaign-monitoring/MonitoringSettingsActions";

// Constants
import { FormDataTargets } from "../../constants/GeneralConstants";

// Components
import BootstrapDateRangePicker from "../bootstrap-date-range-picker/BootstrapDateRangePicker";

// Page Constants
const monitorFrequency = [
  { key: "1", value: "Daily" },
  { key: "2", value: "Once In 2 Days" },
  { key: "3", value: "Once In 3 Days" },
  { key: "5", value: "Once In 5 Days" },
  { key: "7", value: "Weekly Once" },
  { key: "10", value: "Once In 10 Days" },
];

// Page Components
function PersonalDetails({
  contactName,
  contactEmail,
  contactMobile,
  setContactName,
  setContactEmail,
  setContactMobile,
}) {
  const personContactDetails = [
    {
      displayTag: "Name",
      type: "text",
      placeholder: " Name of the person who leads the monitoring",
      setValueFunction: setContactName,
      value: contactName,
      inputBoxStyle: "rounded-lg",
    },

    {
      displayTag: "Email Id",
      type: "staticEmail",
      placeholder: " E.g. joe@email.com",
      setValueFunction: setContactEmail,
      value: contactEmail,
      inputBoxStyle: "rounded-lg",
    },

    {
      displayTag: "Phone No.",
      type: "number",
      placeholder: " (+91) (xxxxxxxxxx)",
      setValueFunction: setContactMobile,
      value: contactMobile,
      inputBoxStyle: "rounded-lg",
    },
  ];

  return (
    <>
      {personContactDetails.map((info, i) => (
        <div key={i} className="form-group row">
          <label className="col-3 col-form-label">{info.displayTag}</label>
          <div className="col-9">
            <input
              className={`form-control shadow-none ${info.inputBoxStyle}`}
              placeholder={info.placeholder}
              value={info.value}
              onChange={({ target }) => info.setValueFunction(target.value)}
            />
          </div>
        </div>
      ))}
    </>
  );
}

function MonitoringDetails({
  dateRange,
  setDateRange,
  frequencyDays,
  setFrequencyDays,
}) {
  // Function
  function onApplyDates(selectedDate) {
    const { startDate, endDate } = selectedDate;
    setDateRange({
      ...dateRange,
      startTimestamp: startDate,
      endTimestamp: endDate,
    });
  }

  return (
    <div className="form-group row">
      <label className="col-3 col-form-label">{"Monitoring Details"}</label>
      <div className="col row mx-0">
        {/* Frequency */}
        <select
          className="form-control shadow-none col-4 rounded-lg"
          placeholder="Frequency"
          id={"frequencyDays"}
          value={frequencyDays}
          onChange={(e) => setFrequencyDays(e.target.value)}
        >
          {monitorFrequency.map((obj) => (
            <option key={obj.key} value={obj.key}>
              {obj.value}
            </option>
          ))}
        </select>

        {/* Date Range Picker */}
        <BootstrapDateRangePicker
          initialStartDate={dateRange.startTimestamp}
          initialEndDate={dateRange.endTimestamp}
          onApplyDates={onApplyDates}
          minDate={new Date()}
          styleObject={{
            buttonClassName: "col px-0 ml-2",
            border: true,
          }}
        />
      </div>
    </div>
  );
}

function MonitoringInstructions({ setInstructions, instructions }) {
  return (
    <div className="form-group row">
      <label className="col-3 col-form-label">{"Notes"}</label>
      <div className="col-9">
        <textarea
          className="form-control shadow-none rounded-lg"
          rows={3}
          value={instructions}
          placeholder=" Enter notes for monitoring person"
          onChange={({ target }) => setInstructions(target.value)}
        ></textarea>
      </div>
    </div>
  );
}

// get Initial Date Range
function getInitialDateRange(defaultDateRange = {}, settings = {}) {
  const { startTimestamp: defaultStartTS, endTimestamp: defaultEndTS } =
    defaultDateRange || {};

  const { startTimestamp: settingsStartTS, endTimestamp: settingsEndTS } =
    settings || {};

  const startTS = defaultStartTS || settingsStartTS;
  const endTS = defaultEndTS || settingsEndTS;

  return {
    startTimestamp: startTS ? new Date(startTS) : new Date(), // today date
    endTimestamp: endTS ? new Date(endTS) : moment().add(1, "days").toDate(), // tomorrow date
  };
}

/**
 * Form- Model
 */
function MediaSetupMonitoringForm({
  onFormSubmit,
  defaultDateRange = {},
  editMonitoringMedia = {}, // initial state (if Edit Monitoring)
}) {
  // Dispatch
  const dispatch = useDispatch();

  // UseSelectors
  const isSetupMonitoring = useSelector(
    (state) => state.monitoringSettings.isSetupMonitoring
  );

  // Edit Monitoring Media Data
  const { monitorUser, settings } = editMonitoringMedia || {};
  const { name, emailId, phoneNo } = monitorUser || {};
  const { frequencyDays: freqDays, instructions: prevInstructions } =
    settings || {};

  // States
  const [contactName, setContactName] = useState(name || "");
  const [contactEmail, setContactEmail] = useState(emailId || "");
  const [contactMobile, setContactMobile] = useState(phoneNo || "");
  const [frequencyDays, setFrequencyDays] = useState(
    freqDays ? String(freqDays) : "1"
  );
  const [instructions, setInstructions] = useState(prevInstructions || "");

  // if component has "monitoringMediaDateObj" as parameter,
  // then the default date will be "mediaDates" else "TodayDate"
  const [dateRange, setDateRange] = useState(
    getInitialDateRange(defaultDateRange, settings)
  );

  // Input Field Bean to pass on Submit Form
  const inputFieldBean = {
    contactName: contactName,
    contactEmail: contactEmail,
    contactMobile: contactMobile,
    frequencyDays: frequencyDays,
    startTimestamp: dateRange.startTimestamp,
    endTimestamp: dateRange.endTimestamp,
    instructions: instructions,
  };

  return (
    <div
      className="modal fade"
      id={FormDataTargets.setupMonitoringForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header bg-alt px-5">
            <h4 className="modal-title">{`${
              isSetupMonitoring ? "Setup" : "Edit"
            } Monitoring Settings`}</h4>
          </div>

          <div className="modal-body px-5">
            {/* "PersonalDetails" component is for first 3 FormInputs */}
            <PersonalDetails
              contactName={contactName}
              contactEmail={contactEmail}
              contactMobile={contactMobile}
              setContactName={setContactName}
              setContactEmail={setContactEmail}
              setContactMobile={setContactMobile}
            />

            {/* monitoring details */}
            <MonitoringDetails
              dateRange={dateRange}
              setFrequencyDays={setFrequencyDays}
              setDateRange={setDateRange}
              frequencyDays={frequencyDays}
            />

            {/* monitoring instructions */}
            <MonitoringInstructions
              instructions={instructions}
              setInstructions={setInstructions}
            />
          </div>

          {/* form Submit and Cancel Button */}
          <div className="modal-footer px-5">
            <button
              type="button"
              className="btn btn-outline-primary btn-action mt-2 mr-3"
              data-dismiss="modal"
              onClick={() => dispatch(closeSetupMonitoringForm())}
            >
              {"Cancel"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-action mt-2"
              onClick={() => {
                onFormSubmit(inputFieldBean);
                dispatch(closeSetupMonitoringForm());
              }}
              data-dismiss="modal"
            >
              {"Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default MediaSetupMonitoringForm;
